import { useReq } from "@larner.dev/use-req";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Input,
  Spinner,
  Text,
} from "@chakra-ui/react";
import {
  Reducer,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { apiReq } from "../../lib/apiReq";
import {
  MAX_MEDIA_BYTES,
  MediaKey,
  ProfilePictureKey,
  UserWithMediaAndGroups,
} from "@greenflagdate/shared";
import { ReqMethod } from "@larner.dev/req";
import imageCompression from "browser-image-compression";
import { store, useStoreData } from "../../lib/store/store";
import { ActionType } from "../../lib/store/storeActions";
import {
  PictureData,
  pictureMeta,
} from "../../pages/marketing/ProfileEditor/pictureMeta";
import { getRuntimeApiUrl } from "../../lib/runtimeApiUrl";
import { convertHeicToJpeg } from "../../lib/convertHeicToJpeg";
import Cropper, {
  Area,
  MediaSize,
  Point,
  Size,
  getInitialCropFromCroppedAreaPercentages,
} from "react-easy-crop";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark, faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  ScreenTransitioner,
  ScreenTransitionerHandle,
} from "../ScreenTransitioner/ScreenTransitioner";
import { ScreenTransitionDirection } from "../ScreenTransitioner/ScreenTransitionDirection";
import headshotAvatar from "../../pages/marketing/ProfileEditor/images/headshot_avatar.png";
import bodyAvatar from "../../pages/marketing/ProfileEditor/images/body_avatar.png";
import actionAvatar from "../../pages/marketing/ProfileEditor/images/action_avatar.png";
import friendsAvatar from "../../pages/marketing/ProfileEditor/images/friends_avatar.png";
import travelAvatar from "../../pages/marketing/ProfileEditor/images/travel_avatar.png";
import petAvatar from "../../pages/marketing/ProfileEditor/images/pet_avatar.png";

interface ProfilePhotosScreenProps {
  onClose?: () => void;
  onComplete: () => void;
}

const avatars: Record<ProfilePictureKey, string> = {
  [ProfilePictureKey.Headshot]: headshotAvatar,
  [ProfilePictureKey.Body]: bodyAvatar,
  [ProfilePictureKey.Action]: actionAvatar,
  [ProfilePictureKey.Friends]: friendsAvatar,
  [ProfilePictureKey.Travel]: travelAvatar,
  [ProfilePictureKey.Pet]: petAvatar,
};

const pictureKeys: ProfilePictureKey[] = [
  ProfilePictureKey.Headshot,
  ProfilePictureKey.Body,
  ProfilePictureKey.Action,
  ProfilePictureKey.Friends,
  ProfilePictureKey.Travel,
  ProfilePictureKey.Pet,
];

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

interface EditorState {
  converting: boolean;
  cropPoint?: Point;
  zoom?: number;
  blobOverride?: Blob | null;
  cropSize?: Size;
  mediaSize?: MediaSize;
}

const editorReducer: Reducer<EditorState, Partial<EditorState>> = (
  state,
  merge
) => ({ ...state, ...merge });

export const ProfilePhotosScreen = ({
  onClose,
  onComplete,
}: ProfilePhotosScreenProps) => {
  const storeData = useStoreData();
  const transitionerRef = useRef<ScreenTransitionerHandle>(null);
  const [activePictureType, setActivePictureType] = useState<
    ProfilePictureKey | undefined
  >();
  const [pictures, setPictures] = useState<
    Partial<Record<ProfilePictureKey, PictureData>>
  >({});

  useEffect(() => {
    if (storeData.user?.media) {
      const keysToDownload = Object.keys(storeData.user.media);
      if (keysToDownload.length) {
        (async () => {
          const blobs = await Promise.all(
            keysToDownload.map(async (key) => {
              const media =
                storeData.user?.media[key as unknown as MediaKey];
              if (media) {
                const url = new URL(
                  media.urls.original,
                  getRuntimeApiUrl()
                );
                const response = await fetch(url, {
                  headers: {
                    Authorization: `Bearer ${storeData.auth.token}`,
                  },
                });
                if (!response.ok) return null;
                const blob = await response.blob();
                return {
                  key,
                  blob,
                  imageSrc: URL.createObjectURL(blob),
                  cropArea: {
                    width: media.crop_width_percent,
                    height: media.crop_height_percent,
                    x: media.crop_x,
                    y: media.crop_y,
                  },
                };
              }
              return null;
            })
          );
          const downloaded: Partial<Record<ProfilePictureKey, PictureData>> =
            blobs
              .filter((b) => b !== null)
              .reduce(
                (agg, { key, ...info }) => ({ ...agg, [key]: info }),
                {}
              );
          setPictures((prev) => ({ ...downloaded, ...prev }));
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [uploadReq] = useReq<UserWithMediaAndGroups>(apiReq);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentCropArea = useRef<Area>({
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });

  const [editorState, dispatch] = useReducer(editorReducer, {
    converting: false,
  });

  const existingBlob = activePictureType
    ? pictures[activePictureType]?.blob
    : undefined;
  const existingCropArea = activePictureType
    ? pictures[activePictureType]?.cropArea
    : undefined;

  const finalImageSrc = useMemo(() => {
    if (editorState.blobOverride === null) return null;
    const calcBlob = editorState.blobOverride || existingBlob;
    if (!calcBlob || !calcBlob.size) return null;
    return URL.createObjectURL(calcBlob);
  }, [existingBlob, editorState.blobOverride]);

  useEffect(() => {
    if (
      editorState.cropSize !== undefined &&
      editorState.mediaSize !== undefined
    ) {
      const { crop, zoom } = getInitialCropFromCroppedAreaPercentages(
        existingCropArea || { height: 100, width: 100, x: 0, y: 0 },
        editorState.mediaSize,
        0,
        editorState.cropSize,
        MIN_ZOOM,
        MAX_ZOOM
      );
      dispatch({ cropPoint: crop, zoom });
    }
  }, [existingCropArea, editorState.cropSize, editorState.mediaSize]);

  const openEditor = useCallback((key: ProfilePictureKey) => {
    setActivePictureType(key);
    dispatch({
      converting: false,
      blobOverride: undefined,
      cropPoint: undefined,
      zoom: undefined,
      cropSize: undefined,
      mediaSize: undefined,
    });
    transitionerRef.current?.setIndex("select");
  }, []);

  const backToGrid = useCallback(() => {
    setActivePictureType(undefined);
    dispatch({
      converting: false,
      blobOverride: undefined,
      cropPoint: undefined,
      zoom: undefined,
      cropSize: undefined,
      mediaSize: undefined,
    });
    transitionerRef.current?.setIndex("grid", {
      direction: ScreenTransitionDirection.Left,
    });
  }, []);

  const handleFileSelect = useCallback(async (file: File) => {
    dispatch({ converting: true });
    const converted = await convertHeicToJpeg(file);
    dispatch({ blobOverride: converted, converting: false });
    transitionerRef.current?.setIndex("crop");
  }, []);

  const handleSave = useCallback(async () => {
    if (!activePictureType || !finalImageSrc) return;
    const blob = (editorState.blobOverride || existingBlob)!;
    const data: PictureData = {
      cropArea: currentCropArea.current,
      imageSrc: finalImageSrc,
      blob,
    };
    setPictures((prev) => ({ ...prev, [activePictureType]: data }));
    backToGrid();
    setUploading(true);

    let uploadBlob = blob;
    if (uploadBlob.size > MAX_MEDIA_BYTES) {
      uploadBlob = await imageCompression(uploadBlob as File, {
        maxSizeMB: MAX_MEDIA_BYTES / 1024 / 1024,
        useWebWorker: true,
      });
    }

    const formData = new FormData();
    formData.append("key", activePictureType);
    formData.append(
      "cropWidthPercentage",
      currentCropArea.current.width.toString()
    );
    formData.append(
      "cropHeightPercentage",
      currentCropArea.current.height.toString()
    );
    formData.append("cropX", currentCropArea.current.x.toString());
    formData.append("cropY", currentCropArea.current.y.toString());
    formData.append("media", uploadBlob, activePictureType);

    const response = await uploadReq[ReqMethod.PUT]("/media", formData);
    setUploading(false);
    if (response.success) {
      store.dispatch({
        type: ActionType.UpdateUser,
        params: { user: response.result as UserWithMediaAndGroups },
      });
    } else {
      setPictures((prev) => {
        const next = { ...prev };
        delete next[activePictureType];
        return next;
      });
    }
  }, [
    activePictureType,
    finalImageSrc,
    editorState.blobOverride,
    existingBlob,
    backToGrid,
    uploadReq,
  ]);

  const handleDelete = useCallback(async () => {
    if (!activePictureType) return;
    const response = await uploadReq[ReqMethod.DELETE](
      `/media/${activePictureType}`,
      {}
    );
    if (response.success) {
      store.dispatch({
        type: ActionType.UpdateUser,
        params: { user: response.result as UserWithMediaAndGroups },
      });
      setPictures((prev) => {
        const next = { ...prev };
        delete next[activePictureType];
        return next;
      });
    }
    backToGrid();
  }, [activePictureType, uploadReq, backToGrid]);

  const hasAtLeastOnePhoto = Object.keys(pictures).length > 0;

  const meta = activePictureType ? pictureMeta[activePictureType] : null;

  return (
    <ScreenTransitioner
      ref={transitionerRef}
      screens={{
        grid: (
          <Box flex={1} px="0.25rem">
            <Text fontWeight="600" mb="0.5rem">
              Add your photos
            </Text>
            <Text fontSize="sm" mb="1rem" color="gray.600">
              Upload at least one photo so others can recognize you. We
              recommend starting with a clear headshot.
            </Text>

            <Flex gap="0.5rem" wrap="wrap">
              {pictureKeys.map((key) => {
                const picture = pictures[key];
                return (
                  <Box
                    key={key}
                    as="button"
                    w="calc(33.33% - 0.34rem)"
                    position="relative"
                    borderStyle="solid"
                    borderWidth="2px"
                    borderRadius="0.3125rem"
                    borderColor="greenDark.solid"
                    bg="transparent"
                    cursor="pointer"
                    overflow="hidden"
                    p={0}
                    onClick={() => openEditor(key)}
                  >
                    <Image
                      src={picture?.imageSrc || avatars[key]}
                      alt={key}
                      w="100%"
                      display="block"
                      aspectRatio="1"
                      objectFit="cover"
                    />
                  </Box>
                );
              })}
            </Flex>

            <Flex
              py="2rem"
              direction={{ base: "column-reverse", sm: "row" }}
              gap="0.65rem"
            >
              {!!onClose && (
                <Button
                  variant="ghost"
                  flex={1}
                  onClick={onClose}
                  type="button"
                  disabled={uploading}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="solid"
                colorPalette="greenDark"
                flex={2}
                disabled={uploading || !hasAtLeastOnePhoto}
                onClick={onComplete}
              >
                {uploading ? "Uploading..." : "Done"}
              </Button>
            </Flex>
          </Box>
        ),

        select: (
          <Box flex={1} px="0.25rem">
            {editorState.converting ? (
              <Flex justify="center" align="center" minH="200px">
                <Spinner size="lg" />
              </Flex>
            ) : meta ? (
              <>
                <Flex gap="0.5rem" mb="1rem">
                  {meta.images.map((img) => (
                    <Box
                      key={img.src}
                      flex={1}
                      position="relative"
                      borderRadius="md"
                      overflow="hidden"
                    >
                      <Image
                        src={img.src}
                        alt={img.type}
                        w="100%"
                        aspectRatio="718 / 423"
                        objectFit="cover"
                      />
                      <Flex
                        position="absolute"
                        top="7%"
                        right="7%"
                        w="1.5rem"
                        h="1.5rem"
                        borderRadius="full"
                        align="center"
                        justify="center"
                        bg={
                          img.type === "good"
                            ? "greenLight.solid"
                            : "red.400"
                        }
                        boxShadow="0px 2px 4px rgba(0, 0, 0, 0.25)"
                      >
                        <FontAwesomeIcon
                          icon={img.type === "good" ? faCheck : faXmark}
                          size="xs"
                          color={
                            img.type === "good"
                              ? "var(--chakra-colors-green-dark-solid, #1a3d1a)"
                              : "var(--chakra-colors-red-800, #9b1c1c)"
                          }
                        />
                      </Flex>
                    </Box>
                  ))}
                </Flex>
                <Heading
                  as="h3"
                  fontSize="lg"
                  fontWeight="600"
                  mb="0.5rem"
                >
                  {meta.title}
                </Heading>
                <Text fontSize="sm" mb="1.5rem" color="gray.600">
                  {meta.description}
                </Text>
              </>
            ) : null}

            <Input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/heic,image/heif"
              display="none"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileSelect(file);
              }}
            />

            <Flex direction="column-reverse" gap="0.65rem">
              <Button variant="ghost" onClick={backToGrid}>
                Back
              </Button>
              <Button
                variant="solid"
                colorPalette="greenDark"
                onClick={() => fileInputRef.current?.click()}
                disabled={editorState.converting}
              >
                Choose Photo
              </Button>
            </Flex>
          </Box>
        ),

        crop: (
          <Box flex={1} px="0.25rem">
            <Box position="relative" w="100%" pb="100%" mb="1rem">
              <Button
                position="absolute"
                top={2}
                right={2}
                zIndex={1}
                size="sm"
                colorPalette="red"
                onClick={handleDelete}
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
              {finalImageSrc && (
                <Cropper
                  image={finalImageSrc}
                  crop={editorState.cropPoint || { x: 0, y: 0 }}
                  zoom={editorState.zoom}
                  aspect={1}
                  onCropChange={(cropPoint) => dispatch({ cropPoint })}
                  onCropComplete={(cropArea) =>
                    (currentCropArea.current = cropArea)
                  }
                  onZoomChange={(zoom) => dispatch({ zoom })}
                  setCropSize={(cropSize) => dispatch({ cropSize })}
                  setMediaSize={(mediaSize) => dispatch({ mediaSize })}
                  minZoom={MIN_ZOOM}
                  maxZoom={MAX_ZOOM}
                />
              )}
            </Box>

            <Flex direction="column-reverse" gap="0.65rem">
              <Button variant="ghost" onClick={backToGrid}>
                Cancel
              </Button>
              <Button
                variant="solid"
                colorPalette="greenDark"
                onClick={handleSave}
              >
                Save
              </Button>
            </Flex>
          </Box>
        ),
      }}
      startIndex="grid"
    />
  );
};
